import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { AppTabularDto } from '../../components/reports/dtos/app-tabular.dto';
import { SortBy } from '../../interfaces/sort-by.interface';
import { getDataProvider } from '../../services/get-data-provider.service';
import { FilterSelectedValue } from '../filters/interfaces/filter-selected-value.interface';

export const useDataProvider = <T,>(
  report_name: string,
  filterSelection: FilterSelectedValue[],
  sortBy: SortBy[] | undefined,
  enabled = true,
  throwOnError = true,
  report_id: string = '',
) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [report_name, report_id, filterSelection, sortBy],
    [report_name, report_id, filterSelection, sortBy],
  );
  const { data, isFetching } = useQuery<AppTabularDto<T>>({
    queryKey,
    queryFn: async () =>
      getDataProvider(
        report_name,
        report_id,
        1,
        10000,
        sortBy,
        filterSelection,
      ),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
    throwOnError,
  });
  // A function to refresh the query.
  const invalidateQuery = useMemo(() => {
    return async () => queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);
  return { data, isFetching, invalidateQuery };
};

export const useDataProviderPaginated = <T,>(
  report_name: string,
  filterSelection: FilterSelectedValue[],
  sortBy: SortBy[] | undefined,
  enabled = true,
  report_id: string,
) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['INFINITE', report_name, report_id, filterSelection, sortBy],
    [report_name, report_id, filterSelection, sortBy],
  );
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery<AppTabularDto<T>>({
      enabled,
      queryKey,
      queryFn: async ({ pageParam }) =>
        getDataProvider(
          report_name,
          report_id,
          pageParam as number,
          100,
          sortBy,
          filterSelection,
        ),
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.loadMore
          ? lastPage.pagination?.currentPage + 1
          : undefined,
      initialPageParam: 1,
      throwOnError: true,
    });
  // A function to refresh the query.
  const invalidateQuery = useMemo(() => {
    return async () => queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);
  return {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    invalidateQuery,
  };
};
